import {ofType} from "redux-observable";
import {mergeMap, switchMap} from "rxjs/operators";

import * as methods from "../constants/methods";
import * as actionTypes from "../constants/actionTypes";
import * as helper from "../constants/helper";
import * as handlers from "../constants/handlers";
import * as helperResponseService from "../services/helperResponseService";
import * as helperEventResponseService from "../services/helperEventResponseService";
import * as windowService from "../services/windowService";
import * as messageHelper from "../helpers/messageHelper";
import * as getterHelper from "../helpers/getterHelper";

const websocketResponse = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.HELPER_WEBSOCKET_RESPONSE),
        mergeMap(({payload}) => {
            let response = null;

            try {
                response = JSON.parse(payload.data);
            } catch (e) {
                response = null;
            }

            if (response === null) {
                windowService.logResponse("Helper", payload.data);
                return [{type: actionTypes.UNKNOWN_HELPER_ACTION}];
            }

            if (response.sequence === undefined || isNaN(response.sequence)) {
                windowService.logResponse("Helper", payload.data);

                if (response.event !== undefined) {
                    return helperEventResponseService.processWebsocketResponse(response, state$.value);
                }

                return [{type: actionTypes.UNKNOWN_HELPER_ACTION}];
            }

            if (state$.value.helper.expected.some((expected) => expected.id === response.sequence)) {
                let expectedRequest = state$.value.helper.expected.find(
                    (expected) => expected.id === response.sequence
                );

                if (expectedRequest === undefined) {
                    windowService.logResponse("Helper", payload.data);
                    return [
                        {
                            type: actionTypes.REMOVE_HELPER_ASYNC_RESPONSE,
                            payload: {
                                id: response.sequence
                            }
                        }
                    ];
                }

                windowService.logResponse("Helper", payload.data, expectedRequest.time);

                return [
                    {
                        type: actionTypes.UNKNOWN_HELPER_ACTION
                    },
                    {
                        type: actionTypes.REMOVE_HELPER_ASYNC_RESPONSE,
                        payload: {
                            id: response.sequence
                        }
                    }
                ];
            }

            let defaultRequests = [
                {
                    type: actionTypes.REMOVE_HELPER_WEBSOCKET_REQUEST,
                    payload: {
                        sequence: response.sequence
                    }
                }
            ];

            let request = state$.value.helper.requests.find((request) => request.id === response.sequence);

            if (request === undefined) {
                windowService.logResponse("Helper", payload.data);
                return defaultRequests;
            }

            windowService.logResponse("Helper", payload.data, request.time);

            return defaultRequests.concat(
                helperResponseService.processWebsocketResponse(request.tabId, request.method, response, state$.value)
            );
        })
    );

const sendBugReport = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SEND_HELPER_BUG_REPORT),
        switchMap(({payload}) => {
            let actions = [
                {
                    type: actionTypes.HELPER_REQUEST,
                    payload: {
                        type: methods.METHOD_HELPER_SEND_ERROR_REPORT,
                        params: {
                            subject: payload.subject,
                            content: payload.content,
                            filePaths: payload.filePaths
                        }
                    },
                    method: handlers.HELPER_SEND_ERROR_REPORT,
                    tabId: 0
                }
            ];

            if (state$.value.module.redlikeSoftware) {
                actions.push({
                    type: actionTypes.SET_BLOCKED_UI,
                    payload: {
                        blocked: true
                    }
                });
            }

            return actions;
        })
    );

const saveLogArchive = (action$) =>
    action$.pipe(
        ofType(actionTypes.SAVE_HELPER_LOG_FILES),
        switchMap(({payload}) => {
            return [
                {
                    type: actionTypes.HELPER_REQUEST,
                    payload: {
                        type: methods.METHOD_HELPER_SAVE_LOG_FILES,
                        params: {
                            path: payload.path
                        }
                    },
                    method: handlers.HELPER_SAVE_LOG_FILES,
                    tabId: 0
                },
                {
                    type: actionTypes.SET_BLOCKED_UI,
                    payload: {
                        blocked: true
                    }
                }
            ];
        })
    );

const prepareLogArchive = (action$) =>
    action$.pipe(
        ofType(actionTypes.HELPER_PREPARE_LOG_FILES),
        switchMap(() => {
            return [
                {
                    type: actionTypes.HELPER_REQUEST,
                    payload: {
                        type: methods.METHOD_HELPER_PREPARE_LOG_FILE
                    },
                    method: handlers.HELPER_PREPARE_LOG_FILE,
                    tabId: 0
                },
                {
                    type: actionTypes.SET_BLOCKED_UI,
                    payload: {
                        blocked: true
                    }
                }
            ];
        })
    );

const prepareFileSuccess = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.HELPER_PREPARE_FILE_SUCCESS),
        switchMap(() => {
            let remaining = state$.value.helper.totalLogArchiveLength - state$.value.helper.downloadedLogArchiveLength;
            if (remaining > helper.HELPER_CHUNK_SIZE) {
                return [
                    {
                        type: actionTypes.HELPER_REQUEST,
                        payload: {
                            type: methods.METHOD_HELPER_GET_PREPARED_LOG_ARCHIVE,
                            params: {
                                bytesFrom: state$.value.helper.downloadedLogArchiveLength,
                                bytesTo: state$.value.helper.downloadedLogArchiveLength + helper.HELPER_CHUNK_SIZE
                            }
                        },
                        method: handlers.HELPER_GET_PREPARED_LOG_ARCHIVE,
                        tabId: 0
                    },
                    {
                        type: actionTypes.HELPER_SAVE_DOWNLOADED_LOG_ARCHIVE_LENGTH,
                        payload: {
                            downloadedLength: state$.value.helper.downloadedLogArchiveLength + helper.HELPER_CHUNK_SIZE
                        }
                    }
                ];
            }

            if (remaining < helper.HELPER_CHUNK_SIZE && remaining > 0) {
                return [
                    {
                        type: actionTypes.HELPER_REQUEST,
                        payload: {
                            type: methods.METHOD_HELPER_GET_PREPARED_LOG_ARCHIVE,
                            params: {
                                bytesFrom: state$.value.helper.downloadedLogArchiveLength,
                                bytesTo: state$.value.helper.totalLogArchiveLength
                            }
                        },
                        method: handlers.HELPER_GET_PREPARED_LOG_ARCHIVE,
                        tabId: 0
                    },
                    {
                        type: actionTypes.HELPER_SAVE_DOWNLOADED_LOG_ARCHIVE_LENGTH,
                        payload: {
                            downloadedLength: state$.value.helper.totalLogArchiveLength
                        }
                    }
                ];
            }

            return [
                {
                    type: actionTypes.HELPER_SAVE_DOWNLOADED_LOG_ARCHIVE_LENGTH,
                    payload: {
                        downloadedLength: state$.value.helper.totalLogArchiveLength
                    }
                },
                {
                    type: actionTypes.HELPER_REQUEST,
                    payload: {
                        type: methods.METHOD_HELPER_RELEASE_LOG_ARCHIVE
                    },
                    method: handlers.HELPER_RELEASE_LOG_ARCHIVE,
                    tabId: 0
                }
            ];
        })
    );

const prepareFileError = (action$) =>
    action$.pipe(
        ofType(actionTypes.HELPER_PREPARE_FILE_ERROR),
        switchMap(() => {
            return [
                {
                    type: actionTypes.SET_BLOCKED_UI,
                    payload: {
                        blocked: false
                    }
                },
                {
                    type: actionTypes.HELPER_REQUEST,
                    payload: {
                        type: methods.METHOD_HELPER_RELEASE_LOG_ARCHIVE
                    },
                    method: handlers.HELPER_RELEASE_LOG_ARCHIVE,
                    tabId: 0
                },
                messageHelper.getErrorMessageAction("flashMessage.problemWithFileExport")
            ];
        })
    );

const syncHelperConfiguration = (action$, state$) =>
    action$.pipe(
        ofType(actionTypes.SYNC_HELPER_CONFIGURATION),
        switchMap(() => {
            if (!state$.value.helper.connected || !state$.value.settings.overwriteHelperConfig) {
                return [];
            }

            let autoConfiguration = getterHelper.synchronizeHelperConfiguration(
                state$.value.helper.configuration,
                state$.value.settings.helperAutoCheck,
                state$.value.settings.helperAutoCheckInterval,
                state$.value.settings.helperAutoDownload,
                state$.value.settings.helperAutoForceInstall,
                state$.value.settings.helperAutoInstall,
                state$.value.settings.helperAutoInstallDelay,
                state$.value.settings.helperAutoInstallNewDbs,
                state$.value.settings.helperAutoNightCheck,
                state$.value.settings.helperAutoRebootDelay
            );

            if (autoConfiguration === null) {
                return [];
            }

            return [
                {
                    type: actionTypes.HELPER_REQUEST,
                    payload: {
                        type: methods.METHOD_HELPER_SET_CONFIGURATION,
                        params: {
                            value: {
                                Auto: autoConfiguration
                            }
                        }
                    },
                    method: handlers.HELPER_SET_CONFIGURATION,
                    tabId: 0
                }
            ];
        })
    );

export default [
    websocketResponse,
    sendBugReport,
    saveLogArchive,
    prepareLogArchive,
    prepareFileSuccess,
    prepareFileError,
    syncHelperConfiguration
];
